import { OUTFIT_CATEGORY_KEY } from '@/constants';
import { OutfitTypeEnum } from '@/enum/outfit_type';
import isEmpty from 'lodash/isEmpty';

const DETAILED_OUTFIT_KEYS: string[] = [
  'top',
  'bottom',
  'one_piece',
  'footwear',
  'sock',
  'outerwear',
];
// Vital detailed outfit keys always need a value or the body part will be missing.
const VITAL_DETAILED_OUTFIT_KEYS: string[] = ['top', 'bottom', 'footwear'];

const EYE_TRAITS_KEYS: string[] = ['eye', 'eye_details', 'eye_size', 'eye_spacing', 'eyelash'];

const IS_TUCKED_KEY: string = 'is_tucked';

const CATEGORY_CUSTOM_COLOUR_KEYS: Record<string, string[]> = {
  top: [
    'top_tone1',
    'top_tone2',
    'top_tone3',
    'top_tone4',
    'top_tone5',
    'top_tone6',
    'top_tone7',
    'top_tone8',
    'top_tone9',
    'top_tone10',
  ],
  bottom: [
    'bottom_tone1',
    'bottom_tone2',
    'bottom_tone3',
    'bottom_tone4',
    'bottom_tone5',
    'bottom_tone6',
    'bottom_tone7',
    'bottom_tone8',
    'bottom_tone9',
    'bottom_tone10',
  ],
  footwear: [
    'footwear_tone1',
    'footwear_tone2',
    'footwear_tone3',
    'footwear_tone4',
    'footwear_tone5',
    'footwear_tone6',
    'footwear_tone7',
    'footwear_tone8',
    'footwear_tone9',
    'footwear_tone10',
  ],
  outerwear: [
    'outerwear_tone1',
    'outerwear_tone2',
    'outerwear_tone3',
    'outerwear_tone4',
    'outerwear_tone5',
    'outerwear_tone6',
    'outerwear_tone7',
    'outerwear_tone8',
    'outerwear_tone9',
    'outerwear_tone10',
  ],
  one_piece: [
    'top_tone1',
    'top_tone2',
    'top_tone3',
    'top_tone4',
    'top_tone5',
    'top_tone6',
    'top_tone7',
    'top_tone8',
    'top_tone9',
    'top_tone10',
  ],
  sock: ['sock_tone1', 'sock_tone2', 'sock_tone3', 'sock_tone4'],
};

const customColourKeys = (): string[] => {
  const customColourKeysArray: string[] = [];
  Object.keys(CATEGORY_CUSTOM_COLOUR_KEYS).forEach(function(key) {
    Array.prototype.push.apply(customColourKeysArray, CATEGORY_CUSTOM_COLOUR_KEYS[key]);
  });
  return customColourKeysArray;
};

const CUSTOM_COLOUR_KEYS: string[] = customColourKeys();

const keysToDelete = (): string[] => {
  return DETAILED_OUTFIT_KEYS.concat(CUSTOM_COLOUR_KEYS)
    .concat(OUTFIT_CATEGORY_KEY)
    .concat(IS_TUCKED_KEY);
};

const detailedOutfitSelectionsToTraits = (
  detailedOutfitSelections: Record<string, number>,
): Record<string, number> => {
  const detailedOutfitTraits: Record<string, number> = {};
  addDetailedOutfitToTraits(detailedOutfitTraits, detailedOutfitSelections);
  addCustomColoursToTraits(detailedOutfitTraits, detailedOutfitSelections);
  addTuckedStateToTraits(detailedOutfitTraits, detailedOutfitSelections);
  detailedOutfitTraits.clothing_type = OutfitTypeEnum.SPLIT;

  return detailedOutfitTraits;
};

const regularOutfitSelectionsToTraits = (
  regularOutfitSelections: number,
): Record<string, number> => {
  return {
    outfit: <number>regularOutfitSelections,
    clothing_type: OutfitTypeEnum.ORIGINAL,
  };
};

const addDetailedOutfitToTraits = (
  detailedOutfitTraits: Record<string, number>,
  detailedOutfitSelections: Record<string, number>,
): void => {
  DETAILED_OUTFIT_KEYS.forEach((detailedOutfitKey) => {
    // If the pre-made outfit does not specify a detailed category, we assume that category value should be 0 (naked).
    if (
      detailedOutfitSelections[detailedOutfitKey] ||
      VITAL_DETAILED_OUTFIT_KEYS.includes(detailedOutfitKey)
    ) {
      detailedOutfitTraits[detailedOutfitKey] = detailedOutfitSelections[detailedOutfitKey]
        ? detailedOutfitSelections[detailedOutfitKey]
        : 0;
    }
  });
};

const addCustomColoursToTraits = (
  detailedOutfitTraits: Record<string, number>,
  detailedOutfitSelections: Record<string, number>,
): void => {
  CUSTOM_COLOUR_KEYS.forEach((customColourKey) => {
    // Handle oneof proto keys
    const oneOfKey = `${customColourKey}_outfit`;
    if (detailedOutfitSelections[oneOfKey]) {
      const mappedKey = detailedOutfitSelections[oneOfKey];
      const value = detailedOutfitSelections[mappedKey];
      if (value || value === 0) {
        detailedOutfitTraits[customColourKey] = value;
      }
    }
  });
};

const addTuckedStateToTraits = (
  detailedOutfitTraits: Record<string, number>,
  detailedOutfitSelections: Record<string, number>,
): void => {
  if (detailedOutfitSelections[IS_TUCKED_KEY] || detailedOutfitSelections[IS_TUCKED_KEY] === 0) {
    detailedOutfitTraits.is_tucked = detailedOutfitSelections[IS_TUCKED_KEY];
  }
};

const isSelected = (
  traits: Record<string, number>,
  detailedOutfitOptions: Record<string, number>,
): boolean => {
  // If the selected outfit is not a detailed outfit, the detailedOutfitOptions outfit is not selected.
  if (traits.clothing_type !== OutfitTypeEnum.SPLIT) {
    return false;
  }

  const selectedOutfit = getDetailedOutfitFromTraits(traits);
  // The selectedOutfit does not have a clothing_type, so remove clothing_type from
  // detailedOutfitOptions so that the outfits can be compared.
  delete detailedOutfitOptions.clothing_type;

  // Compare the detailed outfit traits
  for (const trait of getDetailedOutfitKeys()) {
    // Handle nullable oneof proto key
    const oneOfKey = `${trait}_outfit`;
    let detailedOutfitValue;
    if (!detailedOutfitOptions[oneOfKey]) {
      detailedOutfitValue = detailedOutfitOptions[oneOfKey];
    } else {
      const detailedOutfitKey = detailedOutfitOptions[oneOfKey];
      detailedOutfitValue = detailedOutfitOptions[detailedOutfitKey];
    }

    if (selectedOutfit[trait] !== detailedOutfitValue) {
      return false;
    }
  }
  return true;
};

const getOutfitValuesToSet = (
  type: number,
  outfitSelections: Record<string, number> | number,
): Record<string, number> => {
  switch (type) {
    case OutfitTypeEnum.SPLIT:
      return detailedOutfitSelectionsToTraits(<Record<string, number>>outfitSelections);
    case OutfitTypeEnum.ORIGINAL:
      return regularOutfitSelectionsToTraits(<number>outfitSelections);
    default:
      throw new Error(`Unknown outfit type in getOutfitValuesToSet: ${type}`);
  }
};

const getDetailedOutfitKeys = (): string[] => {
  return DETAILED_OUTFIT_KEYS.concat(CUSTOM_COLOUR_KEYS).concat(IS_TUCKED_KEY);
};

const getDetailedOutfitFromTraits = (traits: Record<string, number>): Record<string, number> => {
  const detailedOutfitTraits: Record<string, number> = {};
  const keys = getDetailedOutfitKeys();
  keys.forEach((key) => {
    if (traits[key] || traits[key] === 0) {
      detailedOutfitTraits[key] = traits[key];
    }
  });
  return detailedOutfitTraits;
};

const isOutfitChanged = (
  initialTraits: Record<string, number>,
  selectedTraits: Record<string, number>,
): boolean => {
  if (!initialTraits || isEmpty(selectedTraits)) {
    return false;
  }
  if (initialTraits.clothing_type !== selectedTraits.clothing_type) {
    return true;
  } else if (selectedTraits.clothing_type === OutfitTypeEnum.ORIGINAL) {
    return initialTraits[OUTFIT_CATEGORY_KEY] !== selectedTraits[OUTFIT_CATEGORY_KEY];
  } else {
    const outfitKeys = DETAILED_OUTFIT_KEYS.concat(CUSTOM_COLOUR_KEYS).concat(IS_TUCKED_KEY);
    for (const outfitKey of outfitKeys) {
      if (initialTraits[outfitKey] !== selectedTraits[outfitKey]) {
        return true;
      }
    }
    return false;
  }
};

export default {
  IS_TUCKED_KEY,
  isSelected,
  getOutfitValuesToSet,
  DETAILED_OUTFIT_KEYS,
  VITAL_DETAILED_OUTFIT_KEYS,
  CATEGORY_CUSTOM_COLOUR_KEYS,
  keysToDelete,
  getDetailedOutfitFromTraits,
  isOutfitChanged,
  EYE_TRAITS_KEYS,
};
